import { useState } from 'react';
import { ArrowLeft, Share, MoreVertical, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePWA } from '@/contexts/PWAContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Install() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isInstalled, isInstallable, showInstallPrompt } = usePWA();
  const [installing, setInstalling] = useState(false);

  const handleInstallClick = async () => {
    setInstalling(true);
    try {
      await showInstallPrompt();
    } finally {
      setInstalling(false);
    }
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-background p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('pwa.install.backToApp')}
        </button>

        <Card className="p-8 text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">
            {t('pwa.install.alreadyInstalled')}
          </h1>
          <p className="text-muted-foreground">
            {t('pwa.install.alreadyInstalledDesc')}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        {t('pwa.install.backToApp')}
      </button>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">{t('pwa.install.title')}</h1>
        <p className="text-muted-foreground mb-8">{t('pwa.install.subtitle')}</p>

        <Tabs defaultValue="ios" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="ios">{t('pwa.install.iphone')}</TabsTrigger>
            <TabsTrigger value="android">{t('pwa.install.android')}</TabsTrigger>
          </TabsList>

          <TabsContent value="ios" className="space-y-6">
            <Card className="p-6">
              <p className="text-sm text-muted-foreground mb-6">
                {t('pwa.install.ios.intro')}
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.ios.step1')}</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('pwa.install.ios.step1Hint')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.ios.step2')}</h3>
                    <div className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <Share className="w-5 h-5 text-primary" />
                      <span className="text-sm">{t('pwa.install.ios.step2Hint')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.ios.step3')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('pwa.install.ios.step3Hint')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    4
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.ios.step4')}</h3>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {t('pwa.install.ios.success')}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="android" className="space-y-6">
            {isInstallable && (
              <Card className="p-6 bg-primary/5 border-primary/20">
                <div className="text-center">
                  <h3 className="font-semibold mb-4 text-lg">
                    {t('pwa.install.android.oneClick')}
                  </h3>
                  <Button
                    onClick={handleInstallClick}
                    disabled={installing}
                    size="lg"
                    className="w-full max-w-xs"
                  >
                    {installing ? (
                      <>
                        <Download className="w-5 h-5 mr-2 animate-bounce" />
                        {t('pwa.install.android.installing')}
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        {t('pwa.install.android.installButton')}
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-4">
                    {t('pwa.install.android.or')}
                  </p>
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h3 className="font-semibold mb-4">{t('pwa.install.android.manualTitle')}</h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.android.step1')}</h3>
                    <div className="inline-flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                      <MoreVertical className="w-5 h-5 text-primary" />
                      <span className="text-sm">{t('pwa.install.android.step1Hint')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.android.step2')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('pwa.install.android.step2Hint')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('pwa.install.android.step3')}</h3>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {t('pwa.install.android.success')}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
